import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import OtpInput from '../components/OtpInput';
import './Auth.css';

const MFAVerify: React.FC = () => {
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isOptional, setIsOptional] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken, logout } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // Check if MFA is optional
    const optional = location.state?.isOptional || false;
    setIsOptional(optional);

    // Try to get mfaToken from location state first
    let mfaToken = location.state?.mfaToken;
    console.log('MFAVerify: Token from location.state:', mfaToken ? mfaToken.substring(0, 30) + '...' : 'NOT FOUND');
    console.log('MFAVerify: Is optional:', optional);

    // If not in state, try to get from sessionStorage (in case of page refresh)
    if (!mfaToken) {
      mfaToken = sessionStorage.getItem('mfaToken');
      console.log('MFAVerify: Token from sessionStorage:', mfaToken ? mfaToken.substring(0, 30) + '...' : 'NOT FOUND');
    }

    if (!mfaToken) {
      console.error('MFAVerify: No token found in state or sessionStorage');
      toast.error('Invalid MFA token. Please login again.');
      setTimeout(() => navigate('/login'), 2000);
    } else {
      // Store token in sessionStorage for page refresh
      sessionStorage.setItem('mfaToken', mfaToken);
      console.log('MFAVerify: Token stored in sessionStorage, length:', mfaToken.length);
    }
  }, [location, navigate]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get mfaToken from state or sessionStorage
      let mfaToken = location.state?.mfaToken || sessionStorage.getItem('mfaToken');

      console.log('MFAVerify: Getting token for verification...');
      console.log('Token from location.state:', location.state?.mfaToken ? 'Found' : 'Not found');
      console.log('Token from sessionStorage:', sessionStorage.getItem('mfaToken') ? 'Found' : 'Not found');

      if (!mfaToken) {
        console.error('MFAVerify: No MFA token found');
        toast.error('MFA token not found. Please login again.');
        sessionStorage.removeItem('mfaToken');
        navigate('/login');
        return;
      }

      console.log('MFAVerify: Sending verification request with token:', mfaToken.substring(0, 30) + '...');
      console.log('MFAVerify: Code to verify:', code);

      const response = await authApi.post('/auth/mfa/verify', {
        code,
        mfaToken,
      });

      const { user, token } = response.data;

      // Store user and token in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Update AuthContext state
      setUser(user);
      setToken(token);

      // Set authorization header
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Clear MFA token from sessionStorage
      sessionStorage.removeItem('mfaToken');

      console.log('MFA verification successful, user logged in:', user.email);
      toast.success('Login successful!');

      // Reset failed attempts on success
      setFailedAttempts(0);

      // Navigate to dashboard
      navigate('/', { replace: true });
    } catch (error: any) {
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);

      // If 3 failed attempts, logout and redirect to login
      if (newFailedAttempts >= 3) {
        toast.error('Too many failed MFA attempts. You have been logged out for security.');
        // Clear all tokens and session data
        sessionStorage.removeItem('mfaToken');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Logout
        logout();
        // Redirect to login
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
        return;
      }

      const remainingAttempts = 3 - newFailedAttempts;
      toast.error(
        error.response?.data?.error || `Invalid MFA code. ${remainingAttempts} attempt(s) remaining.`
      );
      setCode('');
      setLoading(false);
    }
  };

  // Auto-submit as soon as all 6 digits are entered
  useEffect(() => {
    if (code.length === 6 && !loading) {
      formRef.current?.requestSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleSkip = async () => {
    if (!isOptional) {
      return;
    }

    setLoading(true);
    try {
      const mfaToken = location.state?.mfaToken || sessionStorage.getItem('mfaToken');

      if (!mfaToken) {
        toast.error('MFA token not found. Please login again.');
        navigate('/login');
        return;
      }

      const response = await authApi.post('/auth/mfa/skip', {
        mfaToken,
      });

      const { user, token } = response.data;

      // Store user and token in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Update AuthContext state
      setUser(user);
      setToken(token);

      // Set authorization header
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Clear MFA token from sessionStorage
      sessionStorage.removeItem('mfaToken');

      toast.success('Login successful!');
      navigate('/', { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to skip MFA');
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell auth-shell--center">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-icon-badge">
            <Shield size={28} />
          </div>
          <h1>Enter MFA Code</h1>
          <p>
            {isOptional
              ? 'MFA is optional. Enter your 6-digit code for extra security, or skip to login.'
              : 'Enter the 6-digit code from your authenticator app'}
          </p>
        </div>

        <form ref={formRef} onSubmit={handleVerify} className="auth-form">
          <div className="auth-field">
            <OtpInput value={code} onChange={setCode} autoFocus error={failedAttempts > 0} />
            {failedAttempts > 0 && failedAttempts < 3 && (
              <p className="auth-error-text">{3 - failedAttempts} attempt(s) remaining</p>
            )}
          </div>
          <button type="submit" className="btn-primary auth-submit" disabled={loading || code.length !== 6}>
            {loading ? 'Verifying...' : 'Verify and Login'}
          </button>
          {isOptional && (
            <button
              type="button"
              className="btn-secondary auth-submit"
              onClick={handleSkip}
              disabled={loading}
            >
              {loading ? 'Skipping...' : 'Skip MFA and Login'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default MFAVerify;
